import { useCallback, useContext } from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import {
  loadCampaign,
  loadBundleCountries,
  modelToViewModelForCampaign,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import {
  readLocationCriterions,
  readDayTimeCriterions,
} from '@bingads-webui-campaign-react/perf-max-data';
import { ScenarioContext, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { saveCampaign, useBudgetModel } from '@bingads-webui-campaign-react/unified-app-shared';
import { useAppStoreRepository } from '@bingads-webui-campaign-react/app-store-selector';
import { flowToPlatform } from '@bingads-webui-campaign-react/unified-app-assets';

export const useCampaignDetailsStepRepository = (props) => {
  const { state, send } = useWizardService();
  const serverContext = useContext(ServerContext);
  const scenarioContext = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid } = scope.values;
  const { budgetModelToViewModel, budgetViewModelToModel, isBudgetInputValid } = useBudgetModel();

  const { isBudgetAndBiddingEnabled = false } = props || {};

  const { flow } = state.context.wizardData;
  const { searchAppById } = useAppStoreRepository({ platform: flowToPlatform[flow] });

  const load = useCallback(() => {
    const savedCampaign = state.context.wizardData.campaign;
    const { Id: campaignId } = savedCampaign || {};

    const loadPromises = {
      campaign: loadCampaign(cid, aid, campaignId),
      locationTargets: readLocationCriterions(cid, aid, campaignId),
      bundleCountriesAndLocationCriterions: loadBundleCountries({
        cid,
        aid,
        serverContext,
      }),
      dayTimeCriterions: readDayTimeCriterions(cid, aid, campaignId),
    };

    return Promise.props(loadPromises).then(({
      campaign,
      locationTargets,
      bundleCountriesAndLocationCriterions,
      dayTimeCriterions,
    }) => {
      const campaignData = modelToViewModelForCampaign({
        campaignModel: { ...campaign, Status: savedCampaign.status }, // MT sends UserPaused, if we send this back they mark it as active. So we just use Paused from the saved ad model
        locationCriterionsModel: locationTargets ? locationTargets.value : [],
        bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
        dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
      });

      const commonCampaignData = data => ({
        ...bundleCountriesAndLocationCriterions,
        campaign: {
          ...data,
          ManualCPC: _.get(campaign, ['BiddingScheme', 'ManualCPC']) || null,
          ManualCPI: _.get(campaign, ['BiddingScheme', 'ManualCPI']) || null,
        },
        ...(isBudgetAndBiddingEnabled ? budgetModelToViewModel(campaign) : {}),
      });

      if (campaignData.data && campaignData.data.CampaignSettings && campaignData.data.CampaignSettings.length > 0) {
        const appSettings = campaignData.data.CampaignSettings.find(settings => settings['@odata.type'] === '#Model.AppSetting');
        const { AppId } = appSettings || {};

        return searchAppById(AppId).then((appInfo) => {
          campaignData.data.AppSetting = appInfo;
          campaignData.data.AppSettingReadOnly = true;

          return commonCampaignData(campaignData.data);
        });
      }

      return commonCampaignData(campaignData.data);
    }).catch((errorData) => {
      activity.trace(`[UnifiedAppWizard] loadData failed. Error: ${errorData}`);

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, aid, budgetModelToViewModel, cid, isBudgetAndBiddingEnabled, serverContext, state.context.wizardData.campaign, searchAppById]);

  const save = useCallback(({
    data,
  }) => {
    const {
      campaign: campaignData,
      bundleCountries,
      bundleLocationCriterions,
      budgetAmount,
      bidStrategy,
      ManualCPC,
      ManualCPI,
      isBudgetValid,
      isMarketingMetricValid,
    } = data;

    let campaignToSave = campaignData;

    if (isBudgetAndBiddingEnabled) {
      if (!isBudgetInputValid(isBudgetValid, isMarketingMetricValid)) {
        return Promise.reject();
      }

      campaignToSave = {
        ...campaignToSave,
        ...budgetViewModelToModel(bidStrategy, budgetAmount, ManualCPC, ManualCPI),
      };
    }

    const savePromises = {
      campaign: saveCampaign({
        campaign: { data: campaignToSave },
        bundleCountries,
        bundleLocationCriterions,
      }, {
        scenario: scenarioContext.name,
        i18n,
        activityLogger: scenarioContext.createActivity('updateCampaign'),
      }),
    };

    return Promise.props(savePromises).then(({
      campaign: campaignResults,
    }) => {
      activity.trace('[UnifiedAppWizard] - campaign settings updated in campaign details step');
      activity.signal('[UnifiedAppWizard] - campaign settings updated in campaign details step');

      const { campaign } = campaignResults;

      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaign: campaign.data });

      return {
        campaign: campaign.data,
      };
    }).catch((errorData) => {
      activity.trace('[UnifiedAppWizard] - campaign settings update failed in campaign details step');

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, budgetViewModelToModel, i18n, isBudgetAndBiddingEnabled, isBudgetInputValid, scenarioContext, send]);

  return {
    load,
    save,
  };
};
